// Fix: post a reversing entry against a points award, redemption, funding row or bill line. One staff member
// posts it; it takes effect at once and the audit log records who, when, the postings and the reason.
// Accounts, companies, partners and targets are picked by plain name; ids only for funding rows and bill lines.
// CHANGE-070: a confirm states every line and the sums first, and each fix carries an idempotency key, so a second
// press posts it once.
import { useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api, points } from '../../lib'
import { Button, Busy, Err, Fresh, Page, Section, Sources, Table, dayOf, errorText, useKit } from './kit'
import { useStaffAction } from './common'

type Account = { kind: string; label: string; unit: 'points' | 'cents'; needs: 'company' | 'partner' | null }
type Named = { id: string; name: string }
type Options = { accounts: Account[]; targets: { type: string; label: string }[]; companies: Named[]; partners: Named[] }
type Target = { target_type: string; target_id: string; kind: string; memo: string | null; at: string; points_delta: number }
type Line = { kind: string; company_id: string; partner_id: string; amount: string }
const blank = (): Line => ({ kind: 'pts_spendable', company_id: '', partner_id: '', amount: '' })
const KEY = ['staff', 'audit']
// targets a company's activity can offer; the others are typed as ids
const PICKABLE = new Set(['lot', 'redemption'])
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const newKey = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`)

export function FixPage() {
  const kit = useKit()
  const opts = useQuery({ queryKey: ['staff', 'fix-options'], queryFn: () => api<Options>('GET', '/api/staff/fixes/options') })
  const post = useStaffAction<unknown>(() => '/api/staff/fixes', [KEY])
  const [posted, setPosted] = useState<string | null>(null)
  const [targetType, setTargetType] = useState('lot')
  const [targetCompany, setTargetCompany] = useState('')
  const [targetId, setTargetId] = useState('')
  const [reason, setReason] = useState('')
  const [lines, setLines] = useState<Line[]>([blank(), blank()])
  const key = useRef(newKey())   // one key per form: kept until the fix posts or the form changes
  const targets = useQuery({
    queryKey: ['staff', 'fix-targets', targetCompany],
    queryFn: () => api<{ rows: Target[] }>('GET', `/api/staff/fixes/targets?company_id=${targetCompany}`),
    enabled: !!targetCompany && PICKABLE.has(targetType),
  })
  if (opts.isLoading) return <Busy />
  if (opts.error || !opts.data) return <Page title="Fixes"><Err error={opts.error} /></Page>
  const o = opts.data
  const account = (k: string) => o.accounts.find((a) => a.kind === k)
  const touch = () => { key.current = newKey() }
  const set = (i: number, patch: Partial<Line>) => { touch(); setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l))) }
  const unit = (k: string) => account(k)?.unit ?? 'points'
  const sums = lines.reduce<Record<string, number>>((s, l) => {
    const n = /^-?\d+$/.test(l.amount.trim()) ? Number(l.amount) : 0
    s[unit(l.kind)] = (s[unit(l.kind)] ?? 0) + n
    return s
  }, {})
  const balanced = Object.values(sums).every((v) => v === 0) && lines.every((l) => /^-?[1-9]\d*$/.test(l.amount.trim()))
  const picked = lines.every((l) => {
    const needs = account(l.kind)?.needs
    return needs === 'company' ? !!l.company_id : needs === 'partner' ? !!l.partner_id : true
  })
  const hasTarget = UUID_RE.test(targetId.trim())
  const ready = balanced && picked && hasTarget && reason.trim().length >= 3
  const choices = (targets.data?.rows ?? []).filter((t) => t.target_type === targetType)
  const nameOf = (l: Line) => {
    const needs = account(l.kind)?.needs
    return needs === 'company' ? o.companies.find((c) => c.id === l.company_id)?.name
      : needs === 'partner' ? o.partners.find((p) => p.id === l.partner_id)?.name : 'Platform'
  }
  const amountWords = (l: Line) => (unit(l.kind) === 'cents' ? `${l.amount} cents` : `${l.amount} points`)
  const targetLabel = o.targets.find((t) => t.type === targetType)?.label ?? targetType

  async function submit() {
    const ok = await kit.confirm({
      title: 'Post this fix', confirm: 'Post fix', danger: true,
      body: (
        <div className="st-stack">
          <span>{targetLabel} {targetId.trim().slice(0, 8)}. It takes effect at once.</span>
          <ul className="st-fix-confirm">
            {lines.map((l, i) => <li key={i}>{account(l.kind)?.label}, {nameOf(l)}: {amountWords(l)}</li>)}
          </ul>
          <span>Sum: {Object.entries(sums).map(([u, v]) => `${v} ${u}`).join(', ')}</span>
          <span>Why: {reason.trim()}</span>
        </div>
      ),
    })
    if (!ok.ok) return
    post.mutate({
      id: '',
      body: {
        target_type: targetType, target_id: targetId.trim(), reason, idempotency_key: key.current,
        postings: lines.map((l) => {
          const needs = account(l.kind)?.needs
          return {
            kind: l.kind, amount: Number(l.amount),
            company_id: needs === 'company' ? l.company_id || null : null,
            partner_id: needs === 'partner' ? l.partner_id || null : null,
          }
        }),
      },
    }, {
      onSuccess: (r) => { setPosted(String(r.journal_id)); setLines([blank(), blank()]); setReason(''); setTargetId(''); touch() },
      onError: (e) => kit.toast(errorText(e), { bad: true, ms: 6_000 }),
    })
  }

  return (
    <Page title="Fixes" testid="staff-fix" sub="Post a reversing entry. It takes effect at once and the audit log keeps who, when, the lines and why">
      <Sources><Fresh at={opts.dataUpdatedAt ? new Date(opts.dataUpdatedAt) : null} label="Companies and accounts" live limit={600} testid="staff-fix-fresh" /></Sources>
      <Section title="What to fix">
        <div className="st-filters">
          <label className="st-field">Type of entry
            <select className="st-select" value={targetType} onChange={(e) => { touch(); setTargetType(e.target.value); setTargetId('') }} data-testid="staff-fix-target-type">
              {o.targets.map((t) => <option key={t.type} value={t.type}>{t.label}</option>)}
            </select>
          </label>
          {PICKABLE.has(targetType) ? (
            <>
              <label className="st-field">Of
                <select className="st-select" value={targetCompany} onChange={(e) => { touch(); setTargetCompany(e.target.value); setTargetId('') }}
                  data-testid="staff-fix-target-company">
                  <option value="">Pick a company</option>
                  {o.companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>
              {targetCompany ? (
                <label className="st-field">Entry
                  <select className="st-select" value={targetId} onChange={(e) => { touch(); setTargetId(e.target.value) }} data-testid="staff-fix-target-id"
                    disabled={targets.isLoading}>
                    <option value="">{targets.isLoading ? 'Loading' : choices.length ? 'Pick the entry' : 'No entries for this company'}</option>
                    {choices.map((t) => (
                      <option key={t.target_id} value={t.target_id}>
                        {dayOf(t.at)}, {t.memo || t.kind.replace(/[._]/g, ' ')}, {points(t.points_delta)} points
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
            </>
          ) : (
            <label className="st-field">Id
              <input className="st-input st-fix-id" placeholder={`${targetLabel} id`} value={targetId}
                onChange={(e) => { touch(); setTargetId(e.target.value) }} data-testid="staff-fix-target-id" />
            </label>
          )}
        </div>
        {targets.error ? <Err error={targets.error} /> : null}
      </Section>

      <Section title="Lines" right={<span className="st-muted" data-testid="staff-fix-sums">Sum: {Object.entries(sums).map(([u, v]) => `${v} ${u}`).join(', ')}</span>}>
        <Table rows={lines.map((l, i) => ({ l, i }))} rowKey={({ i }) => String(i)}
          rowAttrs={() => ({ 'data-testid': 'staff-fix-line' })}
          columns={[
            { key: 'acct', head: 'Account', cell: ({ l, i }) => (
              <select className="st-select" value={l.kind} onChange={(e) => set(i, { kind: e.target.value })} data-testid="staff-fix-account">
                {o.accounts.map((x) => <option key={x.kind} value={x.kind}>{x.label}</option>)}
              </select>
            ) },
            { key: 'whose', head: 'Whose', cell: ({ l, i }) => {
              const a = account(l.kind)
              if (a?.needs === 'company') return (
                <select className="st-select" value={l.company_id} onChange={(e) => set(i, { company_id: e.target.value })} data-testid="staff-fix-company">
                  <option value="">Pick a company</option>
                  {o.companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              )
              if (a?.needs === 'partner') return (
                <select className="st-select" value={l.partner_id} onChange={(e) => set(i, { partner_id: e.target.value })} data-testid="staff-fix-partner">
                  <option value="">Pick a partner</option>
                  {o.partners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              )
              return <span className="st-muted">Platform</span>
            } },
            { key: 'amt', head: 'Amount, + debit, − credit', cell: ({ l, i }) => (
              <input className="st-input" placeholder={account(l.kind)?.unit === 'cents' ? 'Cents, like -2500' : 'Points, like -500'} value={l.amount}
                onChange={(e) => set(i, { amount: e.target.value })} data-testid="staff-fix-amount" />
            ) },
            { key: 'x', head: '', cell: ({ i }) => lines.length > 2 ? (
              <Button tone="quiet" onClick={() => { touch(); setLines((ls) => ls.filter((_, j) => j !== i)) }}>Remove</Button>
            ) : null },
          ]} />
        <div><Button tone="quiet" data-testid="staff-fix-add-line" onClick={() => { touch(); setLines((ls) => [...ls, blank()]) }}>Add line</Button></div>
      </Section>

      <Section title="Why">
        <textarea className="st-text" placeholder="Reason" value={reason} onChange={(e) => { touch(); setReason(e.target.value) }} data-testid="staff-fix-reason" />
        <div className="st-row">
          <Button tone="primary" disabled={!ready || post.isPending} onClick={() => void submit()} data-testid="staff-fix-post">Post fix</Button>
          {!post.isPending && !ready ? (
            <span className="st-muted" data-testid="staff-fix-missing">
              Still needs {[
                !hasTarget && (PICKABLE.has(targetType) ? 'the entry to fix' : 'a valid id'),
                !picked && 'a company or partner on every line that needs one',
                !balanced && 'whole-number amounts on every line that sum to zero per unit',
                reason.trim().length < 3 && 'a reason',
              ].filter(Boolean).join(', ')}
            </span>
          ) : null}
        </div>
        {posted ? (
          <p className="st-banner" data-testid="staff-fix-posted">
            Posted journal <Link className="st-link" to={`/staff/record/journals/${posted}`}>{posted.slice(0, 8)}</Link>.{' '}
            <Link className="st-link" to="/staff/log?action=ledger.fix">See the audit log</Link>
          </p>
        ) : null}
      </Section>
    </Page>
  )
}
